import { useState, useRef, useEffect, useMemo } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import axios from "axios";
import api, { getArtworks } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import ImageUpload from "../../components/ImageUpload";
import type { Artwork, ArtworkImage } from "../../types/artwork";
import SortableThumbnail from "../../components/admin/SortableThumbnail";

import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";

import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

// Debug: Check if ImageUpload component is imported
console.log("🔧 ImageUpload component imported:", !!ImageUpload);

interface ArtworkFormData {
  title_en: string;
  title_ro: string;

  short_description_en: string;
  short_description_ro: string;

  description_en: string;
  description_ro: string;

  price: string;
  image_url: string; // need images?
  heightCm: string;
  widthCm: string;
  depthCm: string;
  is_featured: boolean;
  is_sold: boolean;
}

const emptyFormData: ArtworkFormData = {
  title_en: "",
  title_ro: "",
  description_en: "",
  description_ro: "",
  short_description_en: "",
  short_description_ro: "",
  price: "0",
  image_url: "", //and images array
  heightCm: "",
  widthCm: "",
  depthCm: "",
  is_featured: false,
  is_sold: false,
};

const getArtworkStatus = (artwork: Artwork) => {
  if (!artwork.is_public_ready) {
    return { label: "Draft", color: "warning" as const };
  }

  if (artwork.is_sold) {
    return { label: "Sold", color: "error" as const };
  }

  if (Number(artwork.price) === 0) {
    return { label: "Unavailable", color: "default" as const };
  }

  if (artwork.is_featured) {
    return { label: "Featured", color: "primary" as const };
  }

  return { label: "Available", color: "success" as const };
};

type ArtworkSortField = "id" | "title" | "price" | "images" | "status";

type SortDirection = "asc" | "desc";
type EditingArtwork = Artwork | { id: number };

function Artworks() {
  const { language } = useLanguage();
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [editingArtwork, setEditingArtwork] = useState<EditingArtwork | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const { user, fetchUserProfile } = useAuth();
  const [formData, setFormData] = useState<ArtworkFormData>(emptyFormData);
  const [uploadedImages, setUploadedImages] = useState<ArtworkImage[]>([]);
  const [deleteMessageOpen, setDeleteMessageOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [artworkToDelete, setArtworkToDelete] = useState<Artwork | null>(null);

  const [originalEditorState, setOriginalEditorState] = useState("");
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const [sortField, setSortField] = useState<ArtworkSortField>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusFilter, setStatusFilter] = useState("All");

  const editorWasOpenRef = useRef(false);
  const createActionRef = useRef<"return" | "continue">("return");
  const [duplicateImageNames, setDuplicateImageNames] = useState<string[]>([]);

  const statusOptions = [
    "All",
    "Draft",
    "Available",
    "Featured",
    "Sold",
    "Unavailable",
  ];

  useEffect(() => {
    /*
     * Capture the form and image state once, when the artwork
     * editor changes from closed to open.
     */
    if (openDialog && !editorWasOpenRef.current) {
      setOriginalEditorState(
        JSON.stringify({
          formData,
          uploadedImages,
        }),
      );
    }

    /*
     * Clear the stored state after the editor has closed.
     */
    if (!openDialog) {
      setOriginalEditorState("");
      setDiscardDialogOpen(false);
    }

    editorWasOpenRef.current = openDialog;
  }, [openDialog, formData, uploadedImages]);

  // Debug logging for dialog state
  useEffect(() => {
    if (openDialog) {
      console.log("🎯 Add Artwork dialog opened");
      console.log("📋 Current formData:", formData);
    }
  }, [openDialog, formData]);

  useEffect(() => {
    let isMounted = true;

    const initializeArtworks = async () => {
      try {
        if (!user) {
          console.log("No user data available");
          if (isMounted) {
            setError("Please log in to access this page");
            setLoading(false);
          }
          return;
        }

        console.log("Artworks component mounted. User:", user);

        if (!user.is_admin) {
          console.log("User is not admin:", user);
          if (isMounted) {
            setError("You must be an admin to view this page");
            setLoading(false);
          }
          return;
        }

        console.log("User is admin, fetching artworks...");
        if (isMounted) {
          await fetchArtworks();
        }
      } catch (error) {
        console.error("Error in initializeArtworks:", error);
        if (isMounted) {
          setError("Failed to initialize artworks page");
          setLoading(false);
        }
      }
    };

    initializeArtworks();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const fetchArtworks = async () => {
    try {
      console.log("Fetching artworks...");

      const artworkData = await getArtworks();

      console.log("Artworks fetched:", artworkData);
      setArtworks(artworkData);
      setError("");
    } catch (error) {
      console.error("Error fetching artworks:", error);

      if (error instanceof Error) {
        setError(`Failed to fetch artworks: ${error.message}`);
      } else {
        setError("Failed to fetch artworks.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: ArtworkSortField) => {
    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortDirection("asc");
  };

  const handleNewestFirst = () => {
    setSortField("id");
    setSortDirection("desc");
  };

  const sortedArtworks = useMemo(() => {
    const filteredArtworks = artworks.filter((artwork) => {
      if (statusFilter === "All") {
        return true;
      }

      return getArtworkStatus(artwork).label === statusFilter;
    });

    return [...filteredArtworks].sort((firstArtwork, secondArtwork) => {
      let firstValue: string | number;
      let secondValue: string | number;

      switch (sortField) {
        case "title":
          firstValue = firstArtwork.title[language]?.toLowerCase() ?? "";

          secondValue = secondArtwork.title[language]?.toLowerCase() ?? "";
          break;

        case "price":
          firstValue = Number(firstArtwork.price);
          secondValue = Number(secondArtwork.price);
          break;

        case "images":
          firstValue =
            firstArtwork.image_count ?? firstArtwork.images?.length ?? 0;

          secondValue =
            secondArtwork.image_count ?? secondArtwork.images?.length ?? 0;
          break;

        case "status":
          firstValue = getArtworkStatus(firstArtwork).label.toLowerCase();

          secondValue = getArtworkStatus(secondArtwork).label.toLowerCase();
          break;

        case "id":
        default:
          firstValue = firstArtwork.id;
          secondValue = secondArtwork.id;
          break;
      }

      let comparison = 0;

      if (typeof firstValue === "number" && typeof secondValue === "number") {
        comparison = firstValue - secondValue;
      } else {
        comparison = String(firstValue).localeCompare(String(secondValue));
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [artworks, language, statusFilter, sortField, sortDirection]);

  const handleEdit = (artwork: Artwork) => {
    setEditingArtwork(artwork);

    setFormData({
      title_en: artwork.title.en,
      title_ro: artwork.title.ro,

      short_description_en: artwork.short_description.en,
      short_description_ro: artwork.short_description.ro,

      description_en: artwork.description.en,
      description_ro: artwork.description.ro,

      price: String(artwork.price),
      image_url: artwork.image_url ?? artwork.imageUrl ?? "",

      heightCm: artwork.heightCm !== null ? String(artwork.heightCm) : "",

      widthCm: artwork.widthCm !== null ? String(artwork.widthCm) : "",

      depthCm: artwork.depthCm !== null ? String(artwork.depthCm) : "",

      is_featured: artwork.is_featured,
      is_sold: artwork.is_sold ?? false,
    });

    const existingImages =
      artwork.images && artwork.images.length > 0
        ? artwork.images
        : artwork.image_url
          ? [
              {
                image_url: artwork.image_url,
                url: artwork.image_url,
                public_id: undefined,
                sort_order: 1,
              },
            ]
          : [];

    setUploadedImages(existingImages);

    setSelectedImageUrl(
      existingImages[0]?.url ?? artwork.image_url ?? artwork.imageUrl ?? "",
    );

    setOpenDialog(true);
  };

  const removeSelectedImage = () => {
    if (!selectedImageUrl) {
      return;
    }

    setUploadedImages((current) => {
      const selectedIndex = current.findIndex(
        (image) => image.url === selectedImageUrl,
      );

      if (selectedIndex === -1) {
        return current;
      }

      const updatedImages = current.filter(
        (image) => image.url !== selectedImageUrl,
      );

      const nextSelectedImage =
        updatedImages[selectedIndex] ??
        updatedImages[selectedIndex - 1] ??
        updatedImages[0];

      setSelectedImageUrl(nextSelectedImage?.url ?? "");

      setFormData((currentForm) => ({
        ...currentForm,
        image_url: updatedImages[0]?.url ?? "",
      }));

      return updatedImages;
    });

    setDeleteMessageOpen(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setUploadedImages((currentImages) => {
      const oldIndex = currentImages.findIndex(
        (image) => image.url === active.id,
      );

      const newIndex = currentImages.findIndex(
        (image) => image.url === over.id,
      );

      if (oldIndex === -1 || newIndex === -1) {
        return currentImages;
      }

      return arrayMove(currentImages, oldIndex, newIndex);
    });
  };

  const moveImage = (currentIndex: number, newIndex: number) => {
    setUploadedImages((current) => {
      if (newIndex < 0 || newIndex >= current.length) {
        return current;
      }

      const updatedImages = [...current];
      const [movedImage] = updatedImages.splice(currentIndex, 1);

      updatedImages.splice(newIndex, 0, movedImage);

      setFormData((currentForm) => ({
        ...currentForm,
        image_url: updatedImages[0]?.url ?? "",
      }));

      return updatedImages;
    });
  };

  const handleDelete = async (artworkId: number) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      await api.delete(`/admin/artworks/${artworkId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchArtworks();
    } catch (error) {
      console.error("Error deleting artworks:", error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          setError("Your session has expired. Please log in again.");
          await fetchUserProfile();
        } else {
          setError(`Failed to delete artworks: ${error.message}`);
        }
      } else if (error instanceof Error) {
        setError(`Failed to delete artworks: ${error.message}`);
      } else {
        setError("Failed to delete artworks.");
      }
    }
  };

  const handleRequestDelete = (artwork: Artwork) => {
    setArtworkToDelete(artwork);
  };

  const handleCancelDelete = () => {
    setArtworkToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!artworkToDelete) {
      return;
    }

    await handleDelete(artworkToDelete.id);
    setArtworkToDelete(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No authentication token found");
      }

      const artworkPayload = {
        title_en: formData.title_en,
        title_ro: formData.title_ro,

        short_description_en: formData.short_description_en,
        short_description_ro: formData.short_description_ro,

        description_en: formData.description_en,
        description_ro: formData.description_ro,

        price: Number(formData.price),

        image_url:
          uploadedImages.length > 0
            ? uploadedImages[0].url
            : formData.image_url,

        images: uploadedImages.map((image) => ({
          image_url: image.url,
          public_id: image.public_id,
        })),

        height_cm: formData.heightCm ? Number(formData.heightCm) : null,

        width_cm: formData.widthCm ? Number(formData.widthCm) : null,

        depth_cm: formData.depthCm ? Number(formData.depthCm) : null,

        is_featured: formData.is_featured,
        is_sold: formData.is_sold,
      };
      if (editingArtwork) {
        await api.put(`/admin/artworks/${editingArtwork.id}`, artworkPayload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        /*
         * Refresh the table data, but keep the Edit Artwork dialog open.
         */
        await fetchArtworks();

        setOriginalEditorState(
          JSON.stringify({
            formData,
            uploadedImages,
          }),
        );

        setOpenDialog(true);
        return;
      }

      /*
       * Create a new artwork, then return to the Artworks table.
       */
      /*
       * Create the artwork. The backend returns the newly
       * created database row, including its ID.
       */
      const response = await api.post("/admin/artworks", artworkPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const createdArtworkId = response.data.id;

      await fetchArtworks();

      if (createActionRef.current === "continue") {
        /*
         * The artwork now exists, so change the open Add dialog
         * into an Edit dialog without clearing the form or images.
         */
        setEditingArtwork({
          id: createdArtworkId,
        });

        setOriginalEditorState(
          JSON.stringify({
            formData,
            uploadedImages,
          }),
        );

        setOpenDialog(true);
        return;
      }

      /*
       * Create & Return keeps the existing behaviour.
       */
      setOpenDialog(false);
      setEditingArtwork(null);
      setUploadedImages([]);
      setSelectedImageUrl("");
      setFormData(emptyFormData);
    } catch (error: any) {
      console.error("Error submitting artwork:", error);
      console.error("Backend response:", error.response?.data);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          setError("Your session has expired. Please log in again.");
          await fetchUserProfile();
        } else {
          setError(
            `Failed to ${
              editingArtwork ? "update" : "create"
            } artwork: ${error.message}`,
          );
        }
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasUnsavedChanges = () => {
    if (!originalEditorState) {
      return false;
    }

    const currentEditorState = JSON.stringify({
      formData,
      uploadedImages,
    });

    return currentEditorState !== originalEditorState;
  };

  const handleRequestCloseEditor = () => {
    if (hasUnsavedChanges()) {
      setDiscardDialogOpen(true);
      return;
    }

    setOpenDialog(false);
  };

  const handleKeepEditing = () => {
    setDiscardDialogOpen(false);
  };

  const handleDiscardChanges = () => {
    if (originalEditorState) {
      const originalState = JSON.parse(originalEditorState) as {
        formData: ArtworkFormData;
        uploadedImages: ArtworkImage[];
      };

      setFormData(originalState.formData);
      setUploadedImages(originalState.uploadedImages);

      setSelectedImageUrl(
        originalState.uploadedImages[0]?.url ??
          originalState.formData.image_url ??
          "",
      );
    }

    setDiscardDialogOpen(false);
  };

  if (loading) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          mt={4}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            createActionRef.current = "return";
            setEditingArtwork(null);
            setUploadedImages([]);
            setSelectedImageUrl("");
            setFormData(emptyFormData);
            setOpenDialog(true);
          }}
        >
          Add Artwork
        </Button>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>

          <Select
            value={statusFilter}
            label="Status"
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="outlined" onClick={handleNewestFirst}>
          Newest First
        </Button>
      </Box>

      {artworks.length === 0 ? (
        <Alert severity="info">
          No artworks found. Add your first artwork!
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "title"}
                    direction={sortField === "title" ? sortDirection : "asc"}
                    onClick={() => handleSort("title")}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>

                <TableCell>Description</TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === "price"}
                    direction={sortField === "price" ? sortDirection : "asc"}
                    onClick={() => handleSort("price")}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>

                <TableCell>Dimensions</TableCell>

                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === "images"}
                    direction={sortField === "images" ? sortDirection : "asc"}
                    onClick={() => handleSort("images")}
                  >
                    Images
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === "status"}
                    direction={sortField === "status" ? sortDirection : "asc"}
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>

                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedArtworks.map((artwork) => {
                const status = getArtworkStatus(artwork);

                return (
                  <TableRow key={artwork.id}>
                    <TableCell>{artwork.title[language]}</TableCell>

                    <TableCell>{artwork.short_description[language]}</TableCell>

                    <TableCell>£{artwork.price}</TableCell>

                    <TableCell>
                      {artwork.heightCm !== null && artwork.widthCm !== null
                        ? `${artwork.heightCm} × ${artwork.widthCm}${
                            artwork.depthCm !== null
                              ? ` × ${artwork.depthCm}`
                              : ""
                          } cm`
                        : "Not set"}
                    </TableCell>

                    <TableCell align="center">
                      {artwork.image_count ?? artwork.images?.length ?? 0}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <IconButton
                        aria-label={`Edit ${artwork.title[language]}`}
                        onClick={() => handleEdit(artwork)}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        aria-label={`Delete ${artwork.title[language]}`}
                        onClick={() => handleRequestDelete(artwork)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={handleRequestCloseEditor}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          {editingArtwork ? "Edit Artwork" : "Add Artwork"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <Grid container spacing={3}>
                {/* Titles */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Title (English)"
                    value={formData.title_en}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        title_en: event.target.value,
                      })
                    }
                    required
                    inputProps={{ minLength: 5 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Title (Romanian)"
                    value={formData.title_ro}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        title_ro: event.target.value,
                      })
                    }
                    required
                    inputProps={{ minLength: 5 }}
                  />
                </Grid>

                {/* Short descriptions */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Short description (English)"
                    value={formData.short_description_en}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        short_description_en: event.target.value,
                      })
                    }
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Short description (Romanian)"
                    value={formData.short_description_ro}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        short_description_ro: event.target.value,
                      })
                    }
                    multiline
                    rows={2}
                  />
                </Grid>

                {/* Long descriptions */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Description (English)"
                    value={formData.description_en}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        description_en: event.target.value,
                      })
                    }
                    multiline
                    rows={4}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Description (Romanian)"
                    value={formData.description_ro}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        description_ro: event.target.value,
                      })
                    }
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
              <TextField
                label="Price"
                type="number"
                value={formData.price}
                onChange={(event) => {
                  const price = event.target.value;

                  setFormData((current) => ({
                    ...current,
                    price,
                    ...(Number(price) <= 0
                      ? {
                          is_featured: false,
                          is_sold: false,
                        }
                      : {}),
                  }));
                }}
                inputProps={{
                  min: 0,
                  step: "0.01",
                }}
                required
              />

              <ImageUpload
                currentImageUrl={
                  selectedImageUrl ||
                  uploadedImages[0]?.url ||
                  formData.image_url
                }
                maxImages={5}
                currentImageCount={uploadedImages.length}
                currentImageUrls={uploadedImages.map((image) => image.url)}
                onDuplicateImages={(imageNames) => {
                  setDuplicateImageNames(imageNames);
                }}
                onImageUploaded={(imageData) => {
                  if (!imageData.url) {
                    return;
                  }

                  // Prevent an existing image being selected again.
                  if (
                    uploadedImages.some((image) => image.url === imageData.url)
                  ) {
                    return;
                  }

                  setUploadedImages((current) => {
                    // Maximum five images per artwork.
                    if (current.length >= 5) {
                      return current;
                    }

                    return [...current, imageData];
                  });

                  // Show the newly added image in the large preview.
                  setSelectedImageUrl(imageData.url);

                  // The first uploaded image becomes the initial main image.
                  setFormData((current) => ({
                    ...current,
                    image_url: current.image_url || imageData.url,
                  }));
                }}
                onRemoveImage={removeSelectedImage}
              />

              {uploadedImages.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Artwork images
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <DndContext
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={uploadedImages.map((image) => image.url)}
                        strategy={horizontalListSortingStrategy}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                          }}
                        >
                          {uploadedImages.map((image, index) => (
                            <SortableThumbnail
                              key={image.url}
                              image={image}
                              index={index}
                              totalImages={uploadedImages.length}
                              moveImage={moveImage}
                              selected={image.url === selectedImageUrl}
                              onSelect={setSelectedImageUrl}
                            />
                          ))}
                        </Box>
                      </SortableContext>
                    </DndContext>
                  </Box>
                </Box>
              )}

              <TextField
                label="Height (cm)"
                type="number"
                value={formData.heightCm}
                onChange={(event) => {
                  const heightCm = event.target.value;

                  setFormData((current) => ({
                    ...current,
                    heightCm,
                    is_featured:
                      Number(heightCm) > 0 && Number(current.widthCm) > 0
                        ? current.is_featured
                        : false,
                  }));
                }}
                inputProps={{ min: 0, step: 0.1 }}
              />

              <TextField
                label="Width (cm)"
                type="number"
                value={formData.widthCm}
                onChange={(event) => {
                  const widthCm = event.target.value;

                  setFormData((current) => ({
                    ...current,
                    widthCm,
                    is_featured:
                      Number(current.heightCm) > 0 && Number(widthCm) > 0
                        ? current.is_featured
                        : false,
                  }));
                }}
                inputProps={{ min: 0, step: 0.1 }}
              />

              <TextField
                label="Depth (cm)"
                type="number"
                value={formData.depthCm}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    depthCm: event.target.value,
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
              />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  component="label"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    disabled={formData.is_sold || Number(formData.price) <= 0}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        is_featured: event.target.checked,
                      })
                    }
                  />

                  <Typography>Featured artwork</Typography>
                </Box>

                <Box
                  component="label"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <input
                    type="checkbox"
                    disabled={Number(formData.price) <= 0}
                    checked={formData.is_sold}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        is_sold: event.target.checked,
                        is_featured: event.target.checked
                          ? false
                          : formData.is_featured,
                      })
                    }
                  />

                  <Typography>Sold</Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              bgcolor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
              py: 2,
              px: 3,
            }}
          >
            <Button
              type="button"
              onClick={handleRequestCloseEditor}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            {editingArtwork ? (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            ) : (
              <>
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={isSubmitting}
                  onClick={() => {
                    createActionRef.current = "continue";
                  }}
                >
                  {isSubmitting && createActionRef.current === "continue"
                    ? "Creating..."
                    : "Create & Continue"}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  onClick={() => {
                    createActionRef.current = "return";
                  }}
                >
                  {isSubmitting && createActionRef.current === "return"
                    ? "Creating..."
                    : "Create & Return"}
                </Button>
              </>
            )}
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={artworkToDelete !== null}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete artwork?</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{artworkToDelete?.title[language]}</strong>?
          </Typography>

          <Typography sx={{ mt: 2 }}>
            This will also remove all images associated with this artwork. This
            action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button type="button" onClick={handleCancelDelete}>
            Cancel
          </Button>

          <Button
            type="button"
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            Delete Artwork
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={discardDialogOpen}
        onClose={handleKeepEditing}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard your changes?</DialogTitle>

        <DialogContent>
          <Typography>Any unsaved changes will be lost.</Typography>
        </DialogContent>

        <DialogActions>
          <Button type="button" onClick={handleKeepEditing}>
            Keep Editing
          </Button>

          <Button
            type="button"
            color="error"
            variant="contained"
            onClick={handleDiscardChanges}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={deleteMessageOpen}
        autoHideDuration={4000}
        onClose={() => setDeleteMessageOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setDeleteMessageOpen(false)}
        >
          Image marked for deletion. Click Save Changes to confirm your changes.
        </Alert>
      </Snackbar>
      <Snackbar
        open={duplicateImageNames.length > 0}
        autoHideDuration={7000}
        onClose={() => setDuplicateImageNames([])}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setDuplicateImageNames([])}
        >
          {duplicateImageNames.length === 1
            ? `The image "${duplicateImageNames[0]}" is already included in this artwork and was not added again.`
            : `The images ${duplicateImageNames
                .map((name) => `"${name}"`)
                .join(
                  ", ",
                )} are already included in this artwork and were not added again.`}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Artworks;
